import { Map } from 'immutable';
import { omit } from 'lodash';
import { createReducer } from '@reduxjs/toolkit';
import { FluxAction } from '@saucelabs/sl-util-redux';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';

import constants from './constants';
import { SESSION_INACTIVE_REASONS } from '../../constants';
import types from './types';
import utils from './utils';
import { ValueOf } from '../../globalTypes';

type LiveSessionManagerState = {
  readonly channel: {
    readonly isEstablished: boolean;
    readonly reconnecting: boolean;
    readonly rfbPath: string;
    readonly screenshotRetrievalInProgress: boolean;
  };
  readonly session: {
    readonly app: appStorageRecords.AppStorageFile | null;
    readonly closedPayload: {
      readonly closedBy: string;
      readonly error: string;
      readonly reason: string | null;
    };
    readonly connectionError: number | null;
    readonly creationError: {
      readonly code: number | null;
      readonly reason: string;
    };
    readonly metadata: Map<string, unknown>;
    readonly id: string | null;
    readonly isInfoLoaded: boolean;
    readonly isReady: boolean;
    readonly owner: string | null;
    readonly secondsLeft: number | null;
  };
}

type CloseSessionPayload = {
  readonly closed_by: ValueOf<typeof constants.SESSION_CLOSED_ORIGINS>; // eslint-disable-line camelcase
  readonly closer_name: string; // eslint-disable-line camelcase
  readonly expired: boolean;
  readonly error: string | null;
}

const initialState: LiveSessionManagerState = {
  channel: {
    isEstablished: false,
    reconnecting: false,
    rfbPath: '',
    screenshotRetrievalInProgress: false,
  },
  session: {
    app: null,
    closedPayload: {
      closedBy: '',
      error: '',
      reason: null,
    },
    connectionError: null,
    creationError: {
      code: null,
      reason: '',
    },
    // TODO: Rename metadata as capabilities
    // and type them properly
    metadata: Map({}),
    id: null,
    isInfoLoaded: false,
    isReady: false,
    owner: null,
    secondsLeft: null,
  },
};

const mapCloseReason = (
  payload: Partial<CloseSessionPayload> = {},
): ValueOf<typeof SESSION_INACTIVE_REASONS> => {
  switch (payload.closed_by) {
    case constants.SESSION_CLOSED_ORIGINS.USER:
      return SESSION_INACTIVE_REASONS.CLOSED_BY_USER;

    case constants.SESSION_CLOSED_ORIGINS.CLIENT:
      return SESSION_INACTIVE_REASONS.INACTIVE;

    case constants.SESSION_CLOSED_ORIGINS.SERVER:
      if (payload.expired) {
        return SESSION_INACTIVE_REASONS.EXPIRED;
      }

      return SESSION_INACTIVE_REASONS.UNKNOWN;

    default:
      return SESSION_INACTIVE_REASONS.UNKNOWN;
  }
};

const liveSessionManagerReducer = createReducer(initialState, (builder) => {
  // We have to explicitly type the case ("<string, FluxAction>")
  // until we have properly typed actions/operations
  builder
    .addCase<string, FluxAction>(types.LSM_SESSION_CREATE.REQUEST, (draft, { payload }) => {
      draft.channel = initialState.channel;
      draft.session = {
        ...initialState.session,
        metadata: Map(utils.getMetaFromFormData(payload)),
      };
    })
    .addCase<string, FluxAction>(types.LSM_SESSION_CREATE.SUCCESS, (draft, { payload }) => {
      draft.session.id = payload.session_id;
      draft.session.isReady = true;
    })
    .addCase<string, FluxAction>(types.LSM_SESSION_CREATE.FAILURE, (draft, { payload }) => {
      draft.session.creationError.code = payload?.response?.status || 500;
      draft.session.creationError.reason = payload?.response?.data?.detail ?? 'Sorry, something unexpected has happened.';
    });

  builder
    .addCase<string, FluxAction>(types.LSM_CHANNEL_STATUS_GET.FAILURE, (draft, { payload }) => {
      draft.session.closedPayload.reason = payload?.reason || null;
      draft.session.connectionError = payload?.code || null;
    });

  builder
    .addCase<string, FluxAction>(types.LSM_CHANNEL_CONNECT.SUCCESS, (draft, { payload }) => {
      draft.channel.isEstablished = true;
      draft.channel.rfbPath = payload?.rfbPath || '';
      draft.session.secondsLeft = payload.secondsLeft;
    });

  builder
    .addCase<string, FluxAction>(types.LSM_GET_SCREENSHOT.REQUEST, (draft) => {
      draft.channel.screenshotRetrievalInProgress = true;
    }).addCase<string, FluxAction>(types.LSM_GET_SCREENSHOT.SUCCESS, (draft) => {
      draft.channel.screenshotRetrievalInProgress = false;
    })
    .addCase<string, FluxAction>(types.LSM_GET_SCREENSHOT.FAILURE, (draft) => {
      draft.channel.screenshotRetrievalInProgress = false;
    });

  builder
    .addCase<string, FluxAction>(types.LSM_GET_SESSION_INFO.SUCCESS, (draft, { payload }) => {
      const newApp = payload.app ?
        new appStorageRecords.AppStorageFile({
          ...omit(payload.app, 'file_name'),
          // temporary workaround the problem that
          // LSM returns a "file_name" key
          // while AppStorageService returns "name"
          name: payload.app.file_name,
        }) :
        draft.session.app;

      draft.session.isInfoLoaded = true;
      draft.session.app = newApp;
      draft.session.owner = payload?.owner?.username;
      draft.session.metadata = draft.session.metadata.merge(
        utils.getMetaFromRemoteCapabilities(payload),
      );
    });

  builder
    .addCase<string, FluxAction>(types.LSM_SECONDS_LEFT_UPDATE, (draft, { payload }) => {
      draft.session.secondsLeft = payload;
    });

  builder
    .addCase<string, FluxAction>(types.LSM_SESSION_RECONNECT.REQUEST, (draft) => {
      draft.channel.reconnecting = true;
    })
    .addCase<string, FluxAction>(types.LSM_SESSION_RECONNECT.SUCCESS, (draft) => {
      draft.channel.reconnecting = false;
    })
    .addCase<string, FluxAction>(types.LSM_SESSION_RECONNECT.FAILURE, (draft) => {
      draft.channel.reconnecting = false;
    });

  builder
    .addCase<string, FluxAction>(types.LSM_SESSION_CLOSE.REQUEST, () => (
      initialState
    ))
    .addCase<string, FluxAction>(types.LSM_SESSION_CLOSE.SUCCESS, (draft, { payload }) => {
      draft.session.closedPayload = {
        closedBy: payload?.closer_name || '',
        error: payload?.error || '',
        reason: mapCloseReason(payload || undefined),
      };
    });
});

export default liveSessionManagerReducer;
export {
  initialState,
  LiveSessionManagerState,
  mapCloseReason,
};
